import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Building2, Users, FileText, Settings, Shield,
  Save, Plus, Trash2, Edit, Download, Upload, Eye,
  Loader2, CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  Euro, Lock, BookOpen, FolderOpen, RefreshCw
} from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { toast } from 'sonner'
import EntitesTab from './EntitesTab'
import ResetPasswordButton from '../../../components/admin/ResetPasswordButton'

const API_URL = process.env.REACT_APP_BACKEND_URL

const TABS = [
  { id: 'parametres', label: 'Paramètres', icon: Settings },
  { id: 'entites', label: 'Entités', icon: Building2 },
  { id: 'utilisateurs', label: 'Utilisateurs', icon: Users },
  { id: 'documents', label: 'Base documentaire', icon: BookOpen },
  { id: 'doctrine', label: 'Doctrine', icon: Shield }
]

const ROLE_CONFIG = {
  master_admin: { label: 'Master Admin', color: 'bg-purple-100 text-purple-700' },
  c2_admin: { label: 'C2 Admin', color: 'bg-indigo-100 text-indigo-700' },
  c2_gestionnaire: { label: 'C2 Gestionnaire', color: 'bg-blue-100 text-blue-700' },
  client_daf: { label: 'DAF', color: 'bg-emerald-100 text-emerald-700' },
}

export default function AdminClientDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [tenant, setTenant] = useState(null)
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('parametres')
  const [saving, setSaving] = useState(false)

  const canAccess = profile?.role === 'master_admin' || profile?.role === 'c2_admin'

  const fetchTenant = useCallback(async () => {
    try {
      const [tenantRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/tenants/${id}`),
        fetch(`${API_URL}/api/admin/tenants/${id}/settings`)
      ])
      
      if (tenantRes.ok) {
        const data = await tenantRes.json()
        setTenant(data.tenant)
      }
      
      if (settingsRes.ok) {
        const data = await settingsRes.json()
        setSettings(data.settings)
      }
    } catch (err) {
      toast.error('Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (canAccess) {
      fetchTenant()
    }
  }, [fetchTenant, canAccess])

  if (!canAccess) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Accès refusé</h2>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Client non trouvé</h2>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-client-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/clients')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">{tenant.nom}</h1>
          <p className="text-sm text-slate-500">
            {tenant.siren && `SIREN: ${tenant.siren}`}
            {tenant.secteur_activite && ` • ${tenant.secteur_activite}`}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex -mb-px">
            {TABS.map(tab => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`${tab.id}-tab`}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    isActive 
                      ? 'border-indigo-600 text-indigo-600' 
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'parametres' && (
            <ParametresTab 
              tenant={tenant} 
              settings={settings} 
              onRefresh={fetchTenant}
            />
          )}
          {activeTab === 'entites' && (
            <EntitesTab tenantId={id} />
          )}
          {activeTab === 'utilisateurs' && (
            <UtilisateursTab 
              tenantId={id} 
              onRefresh={fetchTenant}
            />
          )}
          {activeTab === 'documents' && (
            <DocumentsTab 
              tenantId={id}
            />
          )}
          {activeTab === 'doctrine' && (
            <DoctrineTab 
              settings={settings}
              tenantId={id}
              onRefresh={fetchTenant}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// ONGLET PARAMÈTRES
// ════════════════════════════════════════════════════════════════════════════

function ParametresTab({ tenant, settings, onRefresh }) {
  const [editingSettings, setEditingSettings] = useState(false)
  const [formData, setFormData] = useState({
    budget_amiable_annuel: settings?.budget_amiable_annuel || 50000,
    budget_conservation_annuel: settings?.budget_conservation_annuel || 20000,
    email_notification_principal: settings?.email_notification_principal || '',
    notifications_email: settings?.notifications_email ?? true
  })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenant.id}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      
      if (response.ok) {
        toast.success('Paramètres enregistrés')
        setEditingSettings(false)
        onRefresh()
      } else {
        toast.error('Erreur lors de l\'enregistrement')
      }
    } catch (err) {
      toast.error('Erreur')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Infos générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-800">Informations générales</h3>
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Nom</span>
              <span className="font-medium text-slate-800">{tenant.nom}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">SIREN</span>
              <span className="font-mono">{tenant.siren || '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Secteur</span>
              <span>{tenant.secteur_activite || '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Statut</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                tenant.statut === 'actif' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
              }`}>
                {tenant.statut}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Budgets</h3>
            <button
              onClick={() => setEditingSettings(!editingSettings)}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              {editingSettings ? 'Annuler' : 'Modifier'}
            </button>
          </div>
          
          {editingSettings ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-600 mb-1">Budget amiable annuel (€)</label>
                <input
                  type="number"
                  value={formData.budget_amiable_annuel}
                  onChange={e => setFormData({...formData, budget_amiable_annuel: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">Budget rétention annuel (€)</label>
                <input
                  type="number"
                  value={formData.budget_conservation_annuel}
                  onChange={e => setFormData({...formData, budget_conservation_annuel: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                <Save className="w-4 h-4" />
                Enregistrer
              </button>
            </div>
          ) : (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Budget amiable</span>
                <span className="font-mono font-medium">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(settings?.budget_amiable_annuel || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Consommé</span>
                <span className="font-mono">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(settings?.budget_amiable_consomme || 0)}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full" 
                  style={{ width: `${Math.min(100, ((settings?.budget_amiable_consomme || 0) / (settings?.budget_amiable_annuel || 1)) * 100)}%` }}
                />
              </div>
              
              <div className="pt-3 border-t border-slate-200">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Budget rétention</span>
                  <span className="font-mono font-medium">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(settings?.budget_conservation_annuel || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// ONGLET UTILISATEURS
// ════════════════════════════════════════════════════════════════════════════

function UtilisateursTab({ tenantId, onRefresh }) {
  const [users, setUsers] = useState([])
  const [c2Users, setC2Users] = useState([])
  const [assignments, setAssignments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [entites, setEntites] = useState([])

  const handleEntite = async (userId, entiteId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entite_id: entiteId }) })
      if (!res.ok) throw new Error()
      setUsers(users.map(u => u.id === userId ? { ...u, entite_id: entiteId || null } : u))
      toast.success(entiteId ? 'Acces restreint a l\'entite et ses filles' : 'Acces a tout le client')
    } catch { toast.error('Erreur') }
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const [usersRes, c2Res, assignRes, entRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/tenants/${tenantId}/users`),
          fetch(`${API_URL}/api/admin/users/c2`),
          fetch(`${API_URL}/api/admin/tenants/${tenantId}/assignments`),
          fetch(`${API_URL}/api/entites?tenant_id=${tenantId}&actif=true`)
        ])
        
        if (usersRes.ok) setUsers((await usersRes.json()).users || [])
        if (c2Res.ok) setC2Users((await c2Res.json()).users || [])
        if (assignRes.ok) setAssignments((await assignRes.json()).assignments || [])
        if (entRes.ok) setEntites((await entRes.json()).entites || [])
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [tenantId])

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Désactiver cet utilisateur ?')) return
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        toast.success('Utilisateur désactivé')
        setUsers(users.map(u => u.id === userId ? {...u, statut: 'inactif'} : u))
      }
    } catch (err) {
      toast.error('Erreur')
    }
  }

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
  }

  return (
    <div className="space-y-6">
      {/* Utilisateurs client */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Utilisateurs client</h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </button>
        </div>
        
        {users.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">Aucun utilisateur</p>
        ) : (
          <div className="space-y-2">
            {users.map(user => {
              const roleConfig = ROLE_CONFIG[user.role] || { label: user.role || 'Utilisateur', color: 'bg-slate-100 text-slate-600' }
              return (
                <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-sm font-medium text-slate-600">
                        {user.prenom?.[0] || user.nom?.[0] || user.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">
                        {user.prenom} {user.nom}
                      </p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {entites.length > 0 && (
                      <select value={user.entite_id || ''} onChange={e => handleEntite(user.id, e.target.value)} className="text-xs px-2 py-1 border border-slate-200 rounded bg-white" title="Perimetre : entite (et ses filles) visible par cet utilisateur" data-testid={`user-entite-${user.id}`}>
                        <option value="">Tout le client</option>
                        {entites.map(e => <option key={e.id} value={e.id}>{e.chemin}</option>)}
                      </select>
                    )}
                    <span className={`px-2 py-1 text-xs font-medium rounded ${roleConfig.color}`}>
                      {roleConfig.label}
                    </span>
                    {user.statut === 'inactif' && (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700">
                        Inactif
                      </span>
                    )}
                    <ResetPasswordButton user={user} />
                    <button
                      onClick={() => handleDeactivate(user.id)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Gestionnaires C2 affectés */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Gestionnaires C2 affectés</h3>
          <button
            onClick={() => setShowAssignModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50"
          >
            <Plus className="w-4 h-4" />
            Affecter
          </button>
        </div>
        
        {assignments.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">Aucun gestionnaire affecté</p>
        ) : (
          <div className="space-y-2">
            {assignments.map(a => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-200 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">
                      {a.user_profiles?.prenom} {a.user_profiles?.nom}
                    </p>
                    <p className="text-xs text-slate-500">{a.user_profiles?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-700">
                    {a.niveau_acces}
                  </span>
                  {a.est_gestionnaire_principal && (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700">
                      Principal
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal création utilisateur */}
      {showCreateModal && (
        <CreateUserModal
          tenantId={tenantId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false)
            window.location.reload()
          }}
        />
      )}

      {/* Modal affectation gestionnaire C2 */}
      {showAssignModal && (
        <AssignGestionnaireModal
          tenantId={tenantId}
          c2Users={c2Users}
          existingAssignments={assignments}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => {
            setShowAssignModal(false)
            window.location.reload()
          }}
        />
      )}
    </div>
  )
}

// Modal création utilisateur
function CreateUserModal({ tenantId, onClose, onCreated }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    role: 'client_daf'
  })
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          tenant_id: tenantId
        })
      })
      
      if (response.ok) {
        toast.success('Utilisateur créé')
        onCreated()
      } else {
        const err = await response.json()
        toast.error(err.detail || 'Erreur')
      }
    } catch (err) {
      toast.error('Erreur')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Nouvel utilisateur</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
              <input
                type="text"
                value={formData.prenom}
                onChange={e => setFormData({...formData, prenom: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
              <input
                type="text"
                value={formData.nom}
                onChange={e => setFormData({...formData, nom: e.target.value})}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe *</label>
            <input
              type="password"
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">Minimum 8 caractères</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Rôle *</label>
            <select
              value={formData.role}
              onChange={e => setFormData({...formData, role: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="client_daf">DAF</option>
            </select>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
              Annuler
            </button>
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Modal affectation gestionnaire C2
function AssignGestionnaireModal({ tenantId, c2Users, existingAssignments, onClose, onAssigned }) {
  const [selectedUserId, setSelectedUserId] = useState('')
  const [niveauAcces, setNiveauAcces] = useState('gestionnaire')
  const [principal, setPrincipal] = useState(false)
  const [saving, setSaving] = useState(false)

  const assignedIds = existingAssignments.map(a => a.c2_user_id)
  const availableUsers = c2Users.filter(u => !assignedIds.includes(u.id))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedUserId) return
    setSaving(true)

    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenantId}/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          c2_user_id: selectedUserId,
          niveau_acces: niveauAcces,
          est_gestionnaire_principal: principal
        })
      })

      if (response.ok) {
        toast.success('Gestionnaire affecté')
        onAssigned()
      } else {
        const err = await response.json()
        toast.error(err.detail || 'Erreur')
      }
    } catch (err) {
      toast.error('Erreur')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Affecter un gestionnaire C2</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Gestionnaire *</label>
            {availableUsers.length === 0 ? (
              <p className="text-sm text-slate-500 py-2">Tous les gestionnaires C2 sont déjà affectés</p>
            ) : (
              <select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="assign-user-select"
              >
                <option value="">Sélectionner...</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.prenom} {u.nom} ({u.role})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Niveau d'accès</label>
            <select
              value={niveauAcces}
              onChange={e => setNiveauAcces(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="gestionnaire">Gestionnaire</option>
              <option value="responsable">Responsable</option>
              <option value="lecture">Lecture seule</option>
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={principal}
              onChange={e => setPrincipal(e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-slate-300 rounded"
            />
            <span className="text-sm text-slate-700">Gestionnaire principal</span>
          </label>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !selectedUserId}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              data-testid="assign-submit-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Affecter
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// ONGLET DOCUMENTS (BASE DOCUMENTAIRE)
// ════════════════════════════════════════════════════════════════════════════

function DocumentsTab({ tenantId }) {
  const [documents, setDocuments] = useState([])
  const [categories, setCategories] = useState([])
  const [polices, setPolices] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedPolice, setSelectedPolice] = useState(null)
  const [showUploadModal, setShowUploadModal] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, catsRes, policesRes] = await Promise.all([
        fetch(`${API_URL}/api/kb/documents/${tenantId}`),
        fetch(`${API_URL}/api/kb/categories`),
        fetch(`${API_URL}/api/polices?tenant_id=${tenantId}&statut=actif`)
      ])
      
      if (docsRes.ok) setDocuments((await docsRes.json()).documents || [])
      if (catsRes.ok) setCategories((await catsRes.json()).categories || [])
      if (policesRes.ok) setPolices((await policesRes.json()).polices || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => { fetchData() }, [fetchData])

  const groupedCategories = categories.reduce((acc, cat) => {
    if (!acc[cat.categorie_parent]) acc[cat.categorie_parent] = []
    acc[cat.categorie_parent].push(cat)
    return acc
  }, {})

  const filteredDocs = documents
    .filter(d => !selectedCategory || d.category_code === selectedCategory)
    .filter(d => !selectedPolice || d.police_id === selectedPolice)

  const categoryLabels = {
    contrat: 'Documents contrat',
    doctrine: 'Documents doctrine',
    reference: 'Documents référence'
  }

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Base documentaire</h3>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
          data-testid="btn-add-document"
        >
          <Upload className="w-4 h-4" />
          Ajouter un document
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar catégories + polices */}
        <div className="space-y-4">
          <button
            onClick={() => { setSelectedCategory(null); setSelectedPolice(null) }}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              !selectedCategory && !selectedPolice ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-slate-100 text-slate-700'
            }`}
            data-testid="filter-all-docs"
          >
            Tous les documents ({documents.length})
          </button>
          
          {Object.entries(groupedCategories).map(([parent, cats]) => (
            <div key={parent} className="space-y-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-1">
                {categoryLabels[parent] || parent}
              </p>
              {cats.map(cat => {
                const count = documents.filter(d => d.category_code === cat.code).length
                return (
                  <button
                    key={cat.code}
                    onClick={() => { setSelectedCategory(cat.code); setSelectedPolice(null) }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === cat.code 
                        ? 'bg-indigo-100 text-indigo-700' 
                        : 'hover:bg-slate-100 text-slate-600'
                    }`}
                  >
                    {cat.libelle} {count > 0 && `(${count})`}
                  </button>
                )
              })}
            </div>
          ))}

          {/* Filtre par police */}
          {polices.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-1">
                Par police
              </p>
              {polices.map(p => {
                const count = documents.filter(d => d.police_id === p.id).length
                return (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedPolice(p.id); setSelectedCategory(null) }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedPolice === p.id 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'hover:bg-slate-100 text-slate-600'
                    }`}
                    data-testid={`filter-police-${p.id}`}
                  >
                    <span className="font-medium">{p.numero_police}</span>
                    {count > 0 && <span className="text-xs ml-1">({count})</span>}
                  </button>
                )
              })}
              <button
                onClick={() => { setSelectedPolice('none'); setSelectedCategory(null) }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedPolice === 'none' ? 'bg-amber-100 text-amber-700' : 'hover:bg-slate-100 text-slate-600'
                }`}
              >
                Sans police ({documents.filter(d => !d.police_id).length})
              </button>
            </div>
          )}
        </div>

        {/* Liste documents */}
        <div className="lg:col-span-3 space-y-3">
          {filteredDocs.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun document</p>
            </div>
          ) : (
            filteredDocs.map(doc => (
              <DocumentCard 
                key={doc.id} 
                document={doc} 
                polices={polices}
                onRefresh={fetchData}
              />
            ))
          )}
        </div>
      </div>

      {showUploadModal && (
        <UploadKBDocumentModal
          tenantId={tenantId}
          categories={categories}
          polices={polices}
          onClose={() => setShowUploadModal(false)}
          onUploaded={() => {
            setShowUploadModal(false)
            fetchData()
          }}
        />
      )}
    </div>
  )
}

function DocumentCard({ document, polices = [], onRefresh }) {
  const [extracting, setExtracting] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [editingPolice, setEditingPolice] = useState(false)
  const [selectedPoliceId, setSelectedPoliceId] = useState(document.police_id || '')
  const [savingPolice, setSavingPolice] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [reExtracting, setReExtracting] = useState(false)

  const handleDownload = async () => {
    try {
      const response = await fetch(`${API_URL}/api/kb/document/${document.id}/download-url`)
      if (response.ok) {
        const data = await response.json()
        window.open(data.url, '_blank')
      }
    } catch (err) {
      toast.error('Erreur téléchargement')
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const response = await fetch(`${API_URL}/api/kb/document/${document.id}/permanent`, {
        method: 'DELETE'
      })
      if (response.ok) {
        toast.success('Document supprimé')
        if (onRefresh) onRefresh()
      } else {
        const err = await response.json()
        toast.error(err.detail || 'Erreur suppression')
      }
    } catch (err) {
      toast.error('Erreur suppression')
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleReExtract = async () => {
    setReExtracting(true)
    try {
      const res = await fetch(`${API_URL}/api/kb/document/${document.id}/extract?force=true`, { method: 'POST' })
      if (res.ok) {
        toast.success('Extraction IA relancée')
        if (onRefresh) onRefresh()
      } else {
        toast.error('Erreur extraction IA')
      }
    } catch {
      toast.error('Erreur')
    } finally {
      setReExtracting(false)
    }
  }

  const handleExtract = async (force = false) => {
    setExtracting(true)
    try {
      const response = await fetch(`${API_URL}/api/kb/document/${document.id}/extract?force=${force}`, {
        method: 'POST'
      })
      
      if (response.ok) {
        toast.success('Extraction terminée')
        if (onRefresh) onRefresh()
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Erreur extraction')
      }
    } catch (err) {
      toast.error('Erreur extraction')
    } finally {
      setExtracting(false)
    }
  }

  const handleSavePolice = async () => {
    setSavingPolice(true)
    try {
      const response = await fetch(`${API_URL}/api/kb/document/${document.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ police_id: selectedPoliceId || null })
      })
      if (response.ok) {
        toast.success('Police associée')
        setEditingPolice(false)
        if (onRefresh) onRefresh()
      } else {
        toast.error('Erreur association police')
      }
    } catch (err) {
      toast.error('Erreur')
    } finally {
      setSavingPolice(false)
    }
  }

  const extractionStatus = document.extraction_status || document.statut_extraction
  const canExtract = document.type_document && ['cp', 'cg', 'avenant', 'conditions_particulieres', 'conditions_generales'].includes(document.type_document)
  const isContrat = canExtract

  const statusConfig = {
    pending: { label: 'Extraction auto en cours...', color: 'text-amber-600', bg: 'bg-amber-50', pulse: true },
    processing: { label: document.resume_extraction || 'Analyse IA...', color: 'text-blue-600', bg: 'bg-blue-50', pulse: true },
    completed: { label: 'Extrait', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle },
    error: { label: document.resume_extraction || 'Erreur extraction', color: 'text-red-600', bg: 'bg-red-50', icon: AlertCircle },
    not_applicable: { label: 'Non analysé', color: 'text-slate-400', bg: 'bg-slate-50', icon: null }
  }

  const status = statusConfig[extractionStatus] || statusConfig.not_applicable
  const showReExtract = (extractionStatus === 'error' || extractionStatus === 'not_applicable') && canExtract

  // Confidence score visual
  const confidence = document.extraction_confidence
  const confidenceColor = confidence >= 0.8 ? 'text-emerald-600' : confidence >= 0.5 ? 'text-amber-600' : 'text-red-600'
  const confidenceBg = confidence >= 0.8 ? 'bg-emerald-50' : confidence >= 0.5 ? 'bg-amber-50' : 'bg-red-50'

  // Police associée
  const linkedPolice = polices.find(p => p.id === document.police_id)

  return (
    <div className="bg-white border border-slate-200 rounded-lg hover:border-slate-300 overflow-hidden" data-testid={`doc-card-${document.id}`}>
      <div className="flex items-center gap-4 p-4">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-800 truncate">{document.nom_affichage || document.nom_fichier}</p>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
            <span>{document.type_document || document.knowledge_base_categories?.libelle}</span>
            {document.version > 1 && <span>• v{document.version}</span>}
            {status.label && (
              <span className={`flex items-center gap-1 ${status.color} ${status.bg} px-1.5 py-0.5 rounded ${status.pulse ? 'animate-pulse' : ''}`}>
                {status.icon && <status.icon className="w-3 h-3" />}
                {status.label}
              </span>
            )}
            {confidence > 0 && extractionStatus === 'completed' && (
              <span className={`px-1.5 py-0.5 rounded font-medium ${confidenceColor} ${confidenceBg}`}>
                Confiance {Math.round(confidence * 100)}%
              </span>
            )}
            {linkedPolice && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-medium">
                <Shield className="w-3 h-3" />
                {linkedPolice.numero_police}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {showReExtract && (
            <button
              onClick={handleReExtract}
              disabled={reExtracting}
              title="Relancer l'analyse IA"
              className="p-2 text-amber-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-40"
              data-testid={`btn-reextract-${document.id}`}
            >
              {reExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          )}
          {canExtract && !showReExtract && (
            <button
              onClick={() => handleExtract(extractionStatus === 'completed')}
              disabled={extracting || extractionStatus === 'processing' || extractionStatus === 'pending'}
              title={extractionStatus === 'completed' ? 'Relancer l\'analyse IA' : 'Analyser avec l\'IA'}
              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-40 transition-colors"
              data-testid={`btn-extract-${document.id}`}
            >
              {extracting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          )}
          
          {extractionStatus === 'completed' && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
            >
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
          
          <button
            onClick={handleDownload}
            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
            title="Télécharger"
          >
            <Download className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            title="Supprimer"
            data-testid={`btn-delete-doc-${document.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Confirmation suppression */}
      {showDeleteConfirm && (
        <div className="mx-4 mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700 font-medium mb-2">
            Supprimer définitivement "{document.nom_affichage}" ?
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
              data-testid={`btn-confirm-delete-doc-${document.id}`}
            >
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              Confirmer
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      
      {/* Section association police (documents contrat uniquement) */}
      {isContrat && (
        <div className="px-4 pb-3 border-t border-slate-50 pt-2">
          {editingPolice ? (
            <div className="flex items-center gap-2">
              <select
                value={selectedPoliceId}
                onChange={e => setSelectedPoliceId(e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-lg"
                data-testid={`police-select-${document.id}`}
              >
                <option value="">Aucune police</option>
                {polices.map(p => (
                  <option key={p.id} value={p.id}>{p.numero_police} — {p.type_police}</option>
                ))}
              </select>
              <button
                onClick={handleSavePolice}
                disabled={savingPolice}
                className="px-2 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid={`btn-save-police-${document.id}`}
              >
                {savingPolice ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Sauver'}
              </button>
              <button
                onClick={() => setEditingPolice(false)}
                className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
              >
                Annuler
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditingPolice(true)}
              className="text-xs text-slate-400 hover:text-indigo-600 transition-colors flex items-center gap-1"
              data-testid={`btn-link-police-${document.id}`}
            >
              <Shield className="w-3 h-3" />
              {linkedPolice ? `Police: ${linkedPolice.numero_police}` : 'Associer une police'}
            </button>
          )}
        </div>
      )}
      
      {/* Détails extraction */}
      {showDetails && extractionStatus === 'completed' && document.extraction_data && (
        <div className="px-4 pb-4 pt-0 border-t border-slate-100">
          <div className="mt-3 space-y-3 text-sm">
            {/* Résumé */}
            {document.resume_extraction && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase mb-1">Résumé</p>
                <p className="text-slate-600">{document.resume_extraction}</p>
              </div>
            )}
            
            {/* Stats extraction */}
            <div className="flex flex-wrap gap-3 text-xs">
              {document.extraction_data.garanties?.length > 0 && (
                <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                  {document.extraction_data.garanties.length} garantie(s)
                </span>
              )}
              {document.extraction_data.franchises?.length > 0 && (
                <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                  {document.extraction_data.franchises.length} franchise(s)
                </span>
              )}
              {document.extraction_data.exclusions?.length > 0 && (
                <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full">
                  <AlertCircle className="w-3 h-3" />
                  {document.extraction_data.exclusions.length} exclusion(s)
                </span>
              )}
              {document.extraction_data.plafonds?.length > 0 && (
                <span className="flex items-center gap-1 text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                  {document.extraction_data.plafonds.length} plafond(s)
                </span>
              )}
            </div>
            
            {/* Date, confiance et police */}
            <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
              {document.extraction_date && (
                <span>Analysé le {new Date(document.extraction_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              )}
              {confidence > 0 && (
                <span className={`font-semibold ${confidenceColor}`}>
                  Score: {Math.round(confidence * 100)}%
                </span>
              )}
              {document.extraction_model && (
                <span className="text-slate-400">Modèle: {document.extraction_model}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Modal upload document KB
function UploadKBDocumentModal({ tenantId, categories, polices = [], onClose, onUploaded }) {
  const [file, setFile] = useState(null)
  const [formData, setFormData] = useState({
    category_code: '',
    nom_affichage: '',
    description: '',
    police_id: '',
    visibilite: 'c2_only'
  })
  const [saving, setSaving] = useState(false)
  const [uploadPhase, setUploadPhase] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!file || !formData.category_code) return
    
    // Auto-detect police_id: single police → auto-select, else try filename match
    let targetPoliceId = formData.police_id
    if (!targetPoliceId && polices.length === 1) {
      targetPoliceId = polices[0].id
    }
    if (!targetPoliceId && polices.length > 1) {
      const nums = (file.name.match(/(\d{8,})/g) || [])
      for (const num of nums) {
        const match = polices.find(p => p.numero_police?.includes(num) || num.includes(p.numero_police))
        if (match) { targetPoliceId = match.id; break }
      }
    }
    if (!targetPoliceId && polices.length > 1) {
      toast.error('Selectionnez le contrat')
      return
    }
    // Si pas de police → le backend auto-cree depuis le nom du fichier
    
    setSaving(true)
    setUploadPhase('upload')
    try {
      const data = new FormData()
      data.append('file', file)
      data.append('tenant_id', tenantId)
      data.append('category_code', formData.category_code)
      if (formData.nom_affichage) data.append('nom_affichage', formData.nom_affichage)
      if (formData.description) data.append('description', formData.description)
      if (targetPoliceId) data.append('police_id', targetPoliceId)
      data.append('visibilite', formData.visibilite)
      
      const response = await fetch(`${API_URL}/api/kb/documents/upload`, {
        method: 'POST',
        body: data
      })
      
      if (response.ok) {
        const result = await response.json()
        if (result.extraction_needed) {
          setUploadPhase('extraction')
          toast.success('Document uploadé — Extraction IA en cours...')
          // Wait a moment to show the user extraction is running, then close
          setTimeout(() => {
            onUploaded()
          }, 2000)
        } else {
          toast.success('Document ajouté')
          onUploaded()
        }
      } else {
        const err = await response.json().catch(() => null)
        toast.error(err?.detail || 'Erreur lors de l\'upload')
        setUploadPhase(null)
      }
    } catch (err) {
      toast.error('Erreur réseau')
      setUploadPhase(null)
    } finally {
      setSaving(false)
    }
  }

  const groupedCategories = categories.reduce((acc, cat) => {
    if (!acc[cat.categorie_parent]) acc[cat.categorie_parent] = []
    acc[cat.categorie_parent].push(cat)
    return acc
  }, {})

  // Vérifier si la catégorie sélectionnée est de type "contrat"
  const selectedCat = categories.find(c => c.code === formData.category_code)
  const isContratCategory = selectedCat?.categorie_parent === 'contrat'
  const extractionEnabled = selectedCat?.extraction_ia_activee

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Ajouter un document</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fichier *</label>
            <input
              type="file"
              onChange={e => setFile(e.target.files[0])}
              className="w-full text-sm"
              data-testid="upload-file-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie *</label>
            <select
              value={formData.category_code}
              onChange={e => setFormData({...formData, category_code: e.target.value, police_id: ''})}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="upload-category-select"
            >
              <option value="">Sélectionner...</option>
              {Object.entries(groupedCategories).map(([parent, cats]) => (
                <optgroup key={parent} label={parent}>
                  {cats.map(cat => (
                    <option key={cat.code} value={cat.code}>{cat.libelle}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Police liée — visible seulement si plusieurs contrats (auto-detecte sinon) */}
          {polices.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Contrat
              </label>
              <select
                value={formData.police_id}
                onChange={e => setFormData({...formData, police_id: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="upload-police-select"
              >
                <option value="">Auto-detection par nom de fichier</option>
                {polices.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.numero_police} — {p.libelle || p.type_police}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">Si le nom du fichier contient le numero de contrat, la detection est automatique.</p>
            </div>
          )}
          {polices.length === 1 && (
            <p className="text-xs text-slate-400">Contrat : {polices[0].numero_police} — {polices[0].libelle || polices[0].type_police}</p>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom affiché</label>
            <input
              type="text"
              value={formData.nom_affichage}
              onChange={e => setFormData({...formData, nom_affichage: e.target.value})}
              placeholder="Optionnel"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Visibilité</label>
            <select
              value={formData.visibilite}
              onChange={e => setFormData({...formData, visibilite: e.target.value})}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="c2_only">C2 uniquement</option>
              <option value="daf_c2">DAF + C2</option>
              <option value="tous">Tous</option>
            </select>
          </div>

          {/* Indicateur extraction auto */}
          {extractionEnabled && (
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg text-xs text-indigo-700">
              <Eye className="w-4 h-4 shrink-0" />
              <span>L'extraction IA sera déclenchée automatiquement après l'upload.</span>
            </div>
          )}
          
          {/* Upload progress indicator */}
          {uploadPhase && (
            <div className="px-3 py-3 bg-indigo-50 rounded-lg border border-indigo-100">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-indigo-600 animate-spin shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-indigo-800">
                    {uploadPhase === 'upload' ? 'Upload du document en cours...' : 'Analyse IA en cours...'}
                  </p>
                  <div className="flex gap-3 mt-1.5">
                    <span className={`text-xs font-medium ${uploadPhase === 'upload' ? 'text-indigo-600' : 'text-emerald-600'}`}>
                      {uploadPhase === 'upload' ? '1/2 Upload' : '1/2 Upload'}
                    </span>
                    <span className={`text-xs font-medium ${uploadPhase === 'extraction' ? 'text-indigo-600' : 'text-slate-400'}`}>
                      2/2 Extraction IA
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50">
              Annuler
            </button>
            <button type="submit" disabled={saving || !file} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50" data-testid="upload-submit-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <Upload className="w-4 h-4" />
              {saving ? (uploadPhase === 'extraction' ? 'Analyse IA...' : 'Upload...') : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// ONGLET DOCTRINE
// ════════════════════════════════════════════════════════════════════════════

function DoctrineTab({ settings, tenantId, onRefresh }) {
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    seuil_conservation: settings?.seuil_conservation || 2000,
    seuil_amiable_max: settings?.seuil_amiable_max || 5000,
    seuil_ouverture_assureur: settings?.seuil_ouverture_assureur || 5000,
    seuil_revue_senior: settings?.seuil_revue_senior || 50000,
    seuil_contentieux: settings?.seuil_contentieux || 100000,
    doctrine_personnalisee: settings?.doctrine_personnalisee || false,
    doctrine_notes: settings?.doctrine_notes || ''
  })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenantId}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      
      if (response.ok) {
        toast.success('Doctrine enregistrée')
        setEditing(false)
        onRefresh()
      }
    } catch (err) {
      toast.error('Erreur')
    } finally {
      setSaving(false)
    }
  }

  const seuils = [
    { key: 'seuil_conservation', label: 'Rétention', desc: 'Prise en charge client sans déclaration' },
    { key: 'seuil_amiable_max', label: 'Amiable max', desc: 'Plafond gestion amiable' },
    { key: 'seuil_ouverture_assureur', label: 'Ouverture assureur', desc: 'Seuil déclaration obligatoire' },
    { key: 'seuil_revue_senior', label: 'Revue senior', desc: 'Validation senior requise' },
    { key: 'seuil_contentieux', label: 'Contentieux', desc: 'Passage en contentieux' }
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Paramètres doctrine</h3>
        <button
          onClick={() => setEditing(!editing)}
          className="text-sm text-indigo-600 hover:text-indigo-700"
        >
          {editing ? 'Annuler' : 'Modifier'}
        </button>
      </div>

      <div className="bg-slate-50 rounded-lg p-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.doctrine_personnalisee}
            onChange={e => setFormData({...formData, doctrine_personnalisee: e.target.checked})}
            disabled={!editing}
            className="w-4 h-4 text-indigo-600 border-slate-300 rounded"
          />
          <span className="text-sm font-medium text-slate-700">
            Doctrine personnalisée pour ce client
          </span>
        </label>
        <p className="text-xs text-slate-500 mt-1 ml-6">
          Si activé, les seuils ci-dessous remplacent la doctrine C2 standard
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {seuils.map(({ key, label, desc }) => (
          <div key={key} className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              {editing ? (
                <div className="relative">
                  <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    value={formData[key]}
                    onChange={e => setFormData({...formData, [key]: parseFloat(e.target.value)})}
                    className="w-28 pl-7 pr-2 py-1 border border-slate-200 rounded text-sm text-right"
                  />
                </div>
              ) : (
                <span className="font-mono font-semibold text-slate-800">
                  {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(settings?.[key] || formData[key])}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">{desc}</p>
          </div>
        ))}
      </div>

      {editing && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Notes doctrine</label>
          <textarea
            value={formData.doctrine_notes}
            onChange={e => setFormData({...formData, doctrine_notes: e.target.value})}
            rows={3}
            placeholder="Notes spécifiques à la doctrine de ce client..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
          />
        </div>
      )}

      {editing && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" />
            Enregistrer
          </button>
        </div>
      )}
    </div>
  )
}
